import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, DollarSign, Target } from "lucide-react";
import { toast } from "sonner";

const API = `${BACKEND_URL}/api`;

const TradeHistory = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    fetchTrades();
    fetchStats();
  }, []);

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(response.data);
    } catch (err) {
      console.error("Error fetching trades:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/mt5/account-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
      // Don't show error toast if no MT5 account is connected
      if (err.response?.status !== 400) {
        toast.error("Failed to fetch MT5 stats");
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSyncTrades = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/mt5/sync-trades`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh trades and stats
        await fetchTrades();
        await fetchStats();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("Error syncing trades:", err);
      toast.error(err.response?.data?.message || "Failed to sync trades from MT5");
    } finally {
      setSyncing(false);
    }
  };

  const handleClearHistory = async () => {
    // Confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to clear all trade history? This action cannot be undone."
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/trades/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh trades and stats
        setTrades([]);
        await fetchStats();
      } else {
        toast.error(response.data.message || "Failed to clear history");
      }
    } catch (err) {
      console.error("Error clearing history:", err);
      toast.error(err.response?.data?.message || "Failed to clear trade history");
    }
  };

  const getOutcomeBadge = (outcome) => {
    if (outcome === "win") {
      return <span style={{ color: "#10b981", fontWeight: "600", fontSize: "0.875rem" }}>✓ WIN</span>;
    } else if (outcome === "loss") {
      return <span style={{ color: "#ef4444", fontWeight: "600", fontSize: "0.875rem" }}>✗ LOSS</span>;
    } else if (outcome === "breakeven") {
      return <span style={{ color: "#f59e0b", fontWeight: "600", fontSize: "0.875rem" }}>= BREAKEVEN</span>;
    }
    return <span style={{ color: "#9ca3af", fontSize: "0.875rem" }}>OPEN</span>;
  };

  return (
    <div className="forex-calculator-container">
      <div className="forex-header">
        <h1 className="forex-title" data-testid="history-title">Trade History</h1>
        <p className="forex-subtitle">View all your calculated trades and MT5 account statistics</p>
      </div>

      {/* MT5 Account Stats Card */}
      {stats && (
        <div className="forex-card" style={{ marginBottom: "2rem", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#e8eaed" }}>
              MT5 Account: {stats.mt5_account_number}
            </h2>
            <button
              onClick={handleSyncTrades}
              disabled={syncing}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                background: syncing ? "#4b5563" : "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "0.5rem",
                cursor: syncing ? "not-allowed" : "pointer",
                fontSize: "0.875rem",
                fontWeight: "500"
              }}
            >
              <RefreshCw size={16} className={syncing ? "spin" : ""} />
              {syncing ? "Syncing..." : "Sync MT5 Trades"}
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            {/* Net Profit */}
            <div style={{ padding: "1rem", background: "rgba(255, 255, 255, 0.05)", borderRadius: "0.75rem", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <DollarSign size={20} style={{ color: stats.net_profit >= 0 ? "#10b981" : "#ef4444" }} />
                <span style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Net Profit</span>
              </div>
              <div style={{ fontSize: "1.75rem", fontWeight: "700", color: stats.net_profit >= 0 ? "#10b981" : "#ef4444" }}>
                ${stats.net_profit.toFixed(2)}
              </div>
            </div>

            {/* Winning Percentage */}
            <div style={{ padding: "1rem", background: "rgba(255, 255, 255, 0.05)", borderRadius: "0.75rem", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <Target size={20} style={{ color: "#3b82f6" }} />
                <span style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Win Rate</span>
              </div>
              <div style={{ fontSize: "1.75rem", fontWeight: "700", color: "#e8eaed" }}>
                {stats.winning_percentage.toFixed(1)}%
              </div>
            </div>

            {/* Winning Trades */}
            <div style={{ padding: "1rem", background: "rgba(255, 255, 255, 0.05)", borderRadius: "0.75rem", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <TrendingUp size={20} style={{ color: "#10b981" }} />
                <span style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Wins / Total</span>
              </div>
              <div style={{ fontSize: "1.75rem", fontWeight: "700", color: "#e8eaed" }}>
                {stats.winning_trades} / {stats.closed_trades}
              </div>
            </div>

            {/* Total Profit */}
            <div style={{ padding: "1rem", background: "rgba(255, 255, 255, 0.05)", borderRadius: "0.75rem", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <TrendingUp size={20} style={{ color: "#10b981" }} />
                <span style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Total Profit</span>
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#10b981" }}>
                +${stats.total_profit.toFixed(2)}
              </div>
            </div>

            {/* Total Loss */}
            <div style={{ padding: "1rem", background: "rgba(255, 255, 255, 0.05)", borderRadius: "0.75rem", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <TrendingDown size={20} style={{ color: "#ef4444" }} />
                <span style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Total Loss</span>
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#ef4444" }}>
                -${stats.total_loss.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="forex-card">
        <Link to="/" className="nav-link" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", marginBottom: "2rem" }} data-testid="back-to-calculator">
          <ArrowLeft size={20} />
          Back to Calculator
        </Link>

        {loading ? (
          <div className="loading-spinner" data-testid="loading-spinner">Loading trades...</div>
        ) : trades.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }} data-testid="no-trades">
            <p>No trades calculated yet. Go to the calculator to create your first trade!</p>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <button
                onClick={handleClearHistory}
                style={{
                  padding: "0.5rem 1.5rem",
                  background: "#fbbf24",
                  color: "#000",
                  border: "none",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  transition: "all 0.3s ease",
                  boxShadow: "0 2px 4px rgba(251, 191, 36, 0.3)"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f59e0b";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 8px rgba(251, 191, 36, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#fbbf24";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 2px 4px rgba(251, 191, 36, 0.3)";
                }}
                data-testid="clear-history-button"
              >
                Clear History
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              {trades.map((trade) => (
              <div key={trade.id} className="result-card" data-testid="trade-item">
                <div className="result-header">
                  <div>
                    <span className={`trade-direction ${trade.direction.toLowerCase()}`} data-testid="trade-direction">
                      {trade.direction}
                    </span>
                    <span style={{ marginLeft: "1rem", fontSize: "1.25rem", fontWeight: "700", color: "#e8eaed" }}>
                      {trade.pair}
                    </span>
                    {trade.mt5_account_number && (
                      <span style={{ marginLeft: "1rem", fontSize: "0.875rem", color: "#9ca3af" }}>
                        MT5: {trade.mt5_account_number}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    {getOutcomeBadge(trade.outcome)}
                    <span style={{ color: "#9ca3af", fontSize: "0.875rem" }} data-testid="trade-timestamp">
                      {new Date(trade.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="result-grid" style={{ marginTop: "1.5rem" }}>
                  <div className="result-item">
                    <div className="result-label">Lot Size</div>
                    <div className="result-value" data-testid="trade-lot-size">{trade.lot_size}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Entry Price</div>
                    <div className="result-value" data-testid="trade-entry-price">{trade.entry_price.toFixed(5)}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Stop Loss</div>
                    <div className="result-value" data-testid="trade-stop-loss">{trade.stop_loss.toFixed(5)}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Take Profit</div>
                    <div className="result-value" data-testid="trade-take-profit">{trade.take_profit.toFixed(5)}</div>
                  </div>
                  <div className="result-item">
                    <div className="result-label">Risk Amount</div>
                    <div className="result-value" data-testid="trade-risk-amount">${trade.risk_amount}</div>
                  </div>
                  {trade.actual_profit !== null && trade.actual_profit !== undefined && (
                    <div className="result-item">
                      <div className="result-label">Actual Profit</div>
                      <div className="result-value" style={{ color: trade.actual_profit >= 0 ? "#10b981" : "#ef4444" }}>
                        ${trade.actual_profit.toFixed(2)}
                      </div>
                    </div>
                  )}
                  <div className="result-item">
                    <div className="result-label">Status</div>
                    <div className="result-value" style={{ fontSize: "1rem", textTransform: "capitalize" }} data-testid="trade-status">
                      {trade.status}
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TradeHistory;